import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unifiedLogin, clearError } from '../app/redux/slices/authSlice';
import { fetchEnhancedUserInfo } from '../app/redux/slices/masterSubAccountSlice';
import { useNavigate, Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';
import LanguageSwitcher from '../app/components/LanguageSwitcher';

export default function LoginPage() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { error, loading } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation(['auth', 'nav']);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      return;
    }

    try {
      // Use unified login - tries both regular and sub-account login
      logger.log("LOG: Starting unified login");
      const loginResult = await dispatch(unifiedLogin(credentials)).unwrap();
      
      logger.log("LOG: Login result:", loginResult);
      
      // Fetch enhanced user info ONLY for non-sub-accounts
      // Sub-accounts don't have access to /api/master/getEnhancedUserInfo
      if (loginResult.accountType !== 'sub') {
        logger.log("LOG: Fetching enhanced user info (not a sub-account)");
        try {
          await dispatch(fetchEnhancedUserInfo()).unwrap();
        } catch (userInfoError) {
          logger.warn('Could not fetch enhanced user info:', userInfoError);
          // Continue anyway - account type already set from login
        }
      } else {
        logger.log("LOG: Skipping fetchEnhancedUserInfo (sub-account detected)");
      }
      
      logger.log("LOG: Login successful, redirecting to dashboard");
      navigate('/dashboard');
    } catch (error) {
      logger.error("LOG: Login failed:", error);
      // Error is handled by Redux state
    }
  };

  const handleClearError = () => {
    dispatch(clearError());
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSubmit(e as React.FormEvent);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          {/* Logo and Brand */}
          <div className="flex justify-center items-center space-x-4 mb-6">
            <img 
              src="/icon.png" 
              alt="SmartProbe Logo" 
              width={48} 
              height={48} 
              className="w-12 h-12"
            />
            <h1 className="text-2xl font-bold text-gray-900">{t('nav:logo')}</h1>
          </div>
          
          {/* Language Switcher */}
          <div className="flex justify-center mb-4">
            <LanguageSwitcher />
          </div>
          
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {t('auth:signIn')}
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {t('auth:loginFailed')}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleClearError}
                      className="text-sm bg-red-50 text-red-800 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 px-3 py-1 transition-colors cursor-pointer"
                    >
                      {t('auth:dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                {t('auth:username')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="off"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={t('auth:enterUsername')}
                value={credentials.username}
                onChange={(e) =>
                  setCredentials({ ...credentials, username: e.target.value })
                }
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                {t('auth:password')}
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={t('auth:enterPassword')}
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-900 cursor-pointer z-20"
                tabIndex={-1}
              >
                {showPassword ? (
                  // Eye slash icon (hide password)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  // Eye icon (show password)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              onClick={handleButtonClick}
              disabled={loading || !credentials.username || !credentials.password}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth:loading') : t('auth:loginButton')}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link 
              to="/register" 
              className="font-medium text-blue-600 hover:text-blue-500 text-sm"
            >
              {t('auth:register')}
            </Link>
            <br />
            <Link 
              to="/forgot-password" 
              className="font-medium text-gray-900 hover:text-gray-800 text-sm"
            >
              {t('auth:forgotPassword')}
            </Link>
            <br />
            <Link 
              to="/continue-verification" 
              className="font-medium text-gray-900 hover:text-gray-800 text-sm"
            >
              Continue verification process
            </Link>
          </div>
        </form>
      </div>
      
      {/* AIVision Food Branding - Bottom Right */}
      <div className="fixed bottom-4 right-4 flex items-center space-x-2 text-gray-900">
        <img 
          src="/icon.png" 
          alt="AIVision Food Logo" 
          width={24} 
          height={24} 
          className="w-6 h-6"
        />
        <span className="text-sm font-medium">AIVision Food</span>
      </div>
    </div>
  );
}
